import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  parteEntradaService, sucursalService, almacenService,
  tiposMovimientoService, trabajadorService, tablaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import ImprimirParteModal from '../components/ImprimirParteModal';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';
import '../styles/datatable.css';

const today   = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; };
const fmtN  = (v, d = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmt   = n => isNaN(Number(n)) ? '0.00' : Number(n).toFixed(2);
const ocRef = (serie, numero) => `OC${serie ? '-' + serie.trim() : ''}-${numero}`;

/* ── Input numérico compacto ── */
const NumInput = ({ value, onChange, max, step = '0.0001', min = '0' }) => (
  <input
    type="number" value={value} onChange={onChange} step={step} min={min} max={max}
    style={{
      width: '100%', height: 28, padding: '0 7px', boxSizing: 'border-box',
      textAlign: 'right', fontSize: 12, fontFamily: 'Inter, sans-serif',
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
      borderRadius: 6, color: '#e2e8f0', outline: 'none',
    }}
    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.09)'; e.target.style.boxShadow = 'none'; }}
  />
);

/* ── Autocomplete para filtros — soporta strings simples u objetos {value,label,sublabel} ── */
const FilterAC = ({ value, onChange, options = [], optionItems, placeholder, label }) => {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  const allItems = useMemo(
    () => optionItems || options.map(o => ({ value: o, label: o })),
    [optionItems, options]
  );

  const selectedItem = value ? (allItems.find(o => o.value === value) || { value, label: value }) : null;

  useEffect(() => { if (!value) setQuery(''); }, [value]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter(o =>
      o.label.toLowerCase().includes(q) ||
      (o.sublabel && o.sublabel.toLowerCase().includes(q))
    );
  }, [allItems, query]);

  const select = (item) => { onChange(item.value); setQuery(''); setOpen(false); };
  const clear  = ()     => { onChange('');         setQuery(''); setOpen(false); };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const hasSublabel = !!selectedItem?.sublabel;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        {selectedItem ? (
          /* ── Estado seleccionado ── */
          <div style={{
            minHeight: 30, padding: hasSublabel ? '5px 26px 5px 10px' : '0 26px 0 10px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            borderRadius: 7, boxSizing: 'border-box',
            background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.35)',
          }}>
            <span style={{ fontSize: 12, color: '#60a5fa', fontWeight: 700, lineHeight: 1.3, fontFamily: 'Inter, sans-serif' }}>
              {selectedItem.label}
            </span>
            {hasSublabel && (
              <span style={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.3, marginTop: 1, fontFamily: 'Inter, sans-serif' }}>
                {selectedItem.sublabel}
              </span>
            )}
            <button onClick={clear} style={{
              position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: '#60a5fa',
              fontSize: 16, lineHeight: 1, padding: 2,
            }}>×</button>
          </div>
        ) : (
          /* ── Estado búsqueda ── */
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            style={{
              height: 30, width: '100%', padding: '0 9px', fontSize: 12,
              borderRadius: 7, boxSizing: 'border-box', outline: 'none',
              fontFamily: 'Inter, sans-serif', color: '#e2e8f0',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => { setOpen(true); e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.12)'; }}
            onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.09)'; e.target.style.boxShadow = 'none'; }}
          />
        )}
      </div>
      {open && !selectedItem && filteredItems.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
          background: '#0d1a2e', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 9,
          boxShadow: '0 12px 32px rgba(0,0,0,0.6)', maxHeight: 200, overflowY: 'auto',
        }}>
          {filteredItems.map(item => (
            <div key={item.value} onMouseDown={() => select(item)}
              style={{ padding: item.sublabel ? '8px 12px' : '8px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.09)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>{item.label}</div>
              {item.sublabel && (
                <div style={{ fontSize: 10.5, color: '#64748b', fontFamily: 'Inter, sans-serif', marginTop: 2 }}>{item.sublabel}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Selector de sucursal estilo dropdown custom ── */
const FilterSelect = ({ value, onChange, options, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>{label}</label>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          height: 30, padding: '0 28px 0 10px', borderRadius: 7, boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative',
          background: value ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.05)',
          border: `1px solid ${value ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.09)'}`,
          transition: 'all 0.15s',
        }}
      >
        <span style={{ fontSize: 12, color: value ? '#60a5fa' : '#64748b', fontWeight: value ? 700 : 400, fontFamily: 'Inter, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Todas'}
        </span>
        {value ? (
          <button onMouseDown={e => { e.stopPropagation(); onChange(''); setOpen(false); }} style={{
            position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: '#60a5fa', fontSize: 16, lineHeight: 1, padding: 2,
          }}>×</button>
        ) : (
          <svg style={{ position: 'absolute', right: 9, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.15s', pointerEvents: 'none' }} width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 3.5L5 6.5L8 3.5" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
          background: '#0d1a2e', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 9,
          boxShadow: '0 12px 32px rgba(0,0,0,0.6)', overflow: 'hidden',
        }}>
          <div onMouseDown={() => { onChange(''); setOpen(false); }}
            style={{ padding: '8px 12px', fontSize: 12, color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            Todas las sucursales
          </div>
          {options.map(opt => (
            <div key={opt} onMouseDown={() => { onChange(opt); setOpen(false); }}
              style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif', borderBottom: '1px solid rgba(255,255,255,0.04)',
                color: opt === value ? '#60a5fa' : '#e2e8f0', fontWeight: opt === value ? 700 : 400,
                background: opt === value ? 'rgba(96,165,250,0.08)' : 'transparent',
              }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = 'rgba(96,165,250,0.09)'; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Selector de estado atención PE con badges ── */
const ATENCION_OPCIONES = [
  { value: 'PENDIENTE',        label: 'Pendiente',        cfg: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  } },
  { value: 'ATENDIDO PARCIAL', label: 'Atendido Parcial', cfg: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  } },
  { value: 'ATENDIDO TOTAL',   label: 'Atendido Total',   cfg: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   } },
];

const FilterEstado = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = ATENCION_OPCIONES.find(o => o.value === value) || null;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Estado Atención</label>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          height: 30, padding: '0 28px 0 10px', borderRadius: 7, boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative',
          background: selected ? selected.cfg.bg : 'rgba(255,255,255,0.05)',
          border: `1px solid ${selected ? selected.cfg.border : 'rgba(255,255,255,0.09)'}`,
          transition: 'all 0.15s',
        }}
      >
        {selected ? (
          <span style={{ fontSize: 12, color: selected.cfg.color, fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>{selected.label}</span>
        ) : (
          <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Todos</span>
        )}
        {selected ? (
          <button onMouseDown={e => { e.stopPropagation(); onChange(''); setOpen(false); }} style={{
            position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: selected.cfg.color, fontSize: 16, lineHeight: 1, padding: 2,
          }}>×</button>
        ) : (
          <svg style={{ position: 'absolute', right: 9, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.15s', pointerEvents: 'none' }} width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 3.5L5 6.5L8 3.5" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
          background: '#0d1a2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
          boxShadow: '0 12px 32px rgba(0,0,0,0.6)', overflow: 'hidden',
        }}>
          <div onMouseDown={() => { onChange(''); setOpen(false); }}
            style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Todos los estados</span>
          </div>
          {ATENCION_OPCIONES.map(opt => (
            <div key={opt.value} onMouseDown={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)',
                background: opt.value === value ? opt.cfg.bg : 'transparent', display: 'flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => { if (opt.value !== value) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (opt.value !== value) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                background: opt.cfg.color, flexShrink: 0,
              }} />
              <span style={{ fontSize: 12, color: opt.value === value ? opt.cfg.color : '#e2e8f0', fontWeight: opt.value === value ? 700 : 400, fontFamily: 'Inter, sans-serif' }}>
                {opt.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Badges ── */
const ESTADO_OC = {
  PENDIENTE:   { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
  APROBADO:    { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
  DESAPROBADO: { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  ANULADO:     { color: '#64748b', bg: 'rgba(100,116,139,0.1)',  border: 'rgba(100,116,139,0.2)' },
};
const ATENCION_PE = {
  'PENDIENTE':        { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'ATENDIDO PARCIAL': { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'ATENDIDO TOTAL':   { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
};
const ESTADO_PE = {
  'GENERADO':       { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)'  },
  'APLICADO PAR.':  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)', border: 'rgba(251,146,60,0.3)'  },
  'APLICADO TOTAL': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)'   },
};

const Badge = ({ label, cfg }) => {
  const c = cfg || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 9px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
};

export default function PeDesdeOc() {
  const { sucursal: sesionSucursal } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'form'

  /* ── Lista de OCs ─────────────────────────────────────────────────── */
  const [ocsLista,    setOcsLista]    = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [alert,       setAlert]       = useState(null);

  /* ── Filtros lista ─────────────────────────────────────────────────── */
  const [fSucursal,  setFSucursal]  = useState('');
  const [fSerie,     setFSerie]     = useState('');
  const [fNumero,    setFNumero]    = useState('');
  const [fRuc,       setFRuc]       = useState('');
  const [fEstado,    setFEstado]    = useState('');
  const [fFechaOn,   setFFechaOn]   = useState(true);
  const [fFechaIni,  setFFechaIni]  = useState(weekAgo);
  const [fFechaFin,  setFFechaFin]  = useState(today);

  /* ── Catálogos ────────────────────────────────────────────────────── */
  const [almacenes,       setAlmacenes]       = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [trabajadores,    setTrabajadores]    = useState([]);
  const [tiposDoc,        setTiposDoc]        = useState([]);
  const [centrosCosto,    setCentrosCosto]    = useState([]);
  const [sucursales,      setSucursales]      = useState([]);
  const [loadingCat,      setLoadingCat]      = useState(false);
  const catLoaded = useRef(false);

  /* ── OC seleccionada y sus ítems ──────────────────────────────────── */
  const [ocSeleccionada, setOcSeleccionada] = useState(null);
  const [ocItems,        setOcItems]        = useState([]);
  const [loadingItems,   setLoadingItems]   = useState(false);
  const [seleccionados,  setSeleccionados]  = useState({}); // { id_detalle: cantidad }
  const [soloPendientes, setSoloPendientes] = useState(false);

  /* ── Formulario ───────────────────────────────────────────────────── */
  const [form, setForm] = useState({
    id_sucursal: '', id_almacen: '', id_tipo_movimiento: '',
    fecha_operacion: today(),
    id_tipo_doc_ref1: '', num_doc_ref1: '',
    id_tipo_doc_ref2: '', num_doc_ref2: '',
    observacion: '', id_centro_costo: '', id_solicitante: '',
  });
  const [nextNumero,  setNextNumero]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [creado,      setCreado]      = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);

  const tdSvc = useRef(tablaService('tipos-documento'));
  const ccSvc = useRef(tablaService('centros-costo'));

  /* ── Carga de OCs ─────────────────────────────────────────────────── */
  const fetchOcsLista = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await parteEntradaService.getOcsParaPe();
      setOcsLista(r.data || []);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar las órdenes de compra.' });
    } finally {
      setLoadingList(false);
    }
  }, []);

  /* ── Volver a la lista refrescando los estados de atención ───────── */
  const volverALista = useCallback(() => {
    setView('list');
    fetchOcsLista();
  }, [fetchOcsLista]);

  /* ── Carga de catálogos ───────────────────────────────────────────── */
  const fetchCatalogos = useCallback(async () => {
    if (catLoaded.current) return;
    setLoadingCat(true);
    try {
      const [suc, alm, tm, trab, td, cc] = await Promise.all([
        sucursalService.getAll(),
        almacenService.getAll(),
        tiposMovimientoService.getAll(),
        trabajadorService.getAll(),
        tdSvc.current.getAll(),
        ccSvc.current.getAll(),
      ]);
      setSucursales(suc.data || []);
      setAlmacenes(alm.data  || []);
      const tmEnt = (tm.data || []).filter(t => t.tipo === 'ENTRADA');
      setTiposMovimiento(tmEnt);
      setTrabajadores(trab.data || []);
      setTiposDoc(td.data     || []);
      setCentrosCosto(cc.data || []);
      catLoaded.current = true;
      const co = tmEnt.find(t => t.codigo === 'CO');
      if (co) setForm(f => ({ ...f, id_tipo_movimiento: String(co.id_tipo_movimiento) }));
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => {
    fetchOcsLista();
    fetchCatalogos();
  }, [fetchOcsLista, fetchCatalogos]);

  /* ── Correlativo ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!form.id_almacen) { setNextNumero(null); return; }
    parteEntradaService.getNextNumero(form.id_almacen)
      .then(r => setNextNumero(r.data.numero))
      .catch(() => setNextNumero(null));
  }, [form.id_almacen]);

  /* ── Seleccionar OC desde la lista ───────────────────────────────── */
  const seleccionarOc = async (oc) => {
    fetchCatalogos();
    setOcSeleccionada(oc);
    setSeleccionados({});
    setOcItems([]);
    setSoloPendientes(false);
    const co = tiposMovimiento.find(t => t.codigo === 'CO');
    setForm({
      id_sucursal:        sesionSucursal?.id_sucursal ? String(sesionSucursal.id_sucursal) : '',
      id_almacen:         '',
      id_tipo_movimiento: co ? String(co.id_tipo_movimiento) : '',
      fecha_operacion:    today(),
      id_tipo_doc_ref1:   oc.id_tipo_doc_ref1 ? String(oc.id_tipo_doc_ref1) : '',
      num_doc_ref1:       oc.num_doc_ref1 || '',
      id_tipo_doc_ref2:   oc.id_tipo_doc_ref2 ? String(oc.id_tipo_doc_ref2) : '',
      num_doc_ref2:       oc.num_doc_ref2 || '',
      observacion:        '',
      id_centro_costo:    '',
      id_solicitante:     '',
    });
    setNextNumero(null);
    setCreado(null);
    setView('form');
    setLoadingItems(true);
    try {
      const res = await parteEntradaService.getOcItems(oc.id_orden);
      setOcItems(res.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar ítems de la OC.') });
    } finally {
      setLoadingItems(false);
    }
  };

  const almacenesSucursal = useMemo(
    () => sesionSucursal?.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(sesionSucursal.id_sucursal))
      : almacenes,
    [almacenes, sesionSucursal]
  );

  /* ── Filtrado de la lista de OCs ─────────────────────────────────── */
  const ocsListaFiltrada = useMemo(() => {
    return ocsLista.filter(oc => {
      if (fSucursal && oc.sucursal_nombre !== fSucursal) return false;
      if (fSerie    && !(oc.serie_codigo || '').toLowerCase().includes(fSerie.toLowerCase())) return false;
      if (fNumero   && !String(oc.numero).includes(fNumero)) return false;
      if (fRuc      && oc.proveedor_ruc !== fRuc) return false;
      if (fEstado   && oc.atencion_pe !== fEstado) return false;
      if (fFechaOn) {
        const fecha = (oc.fecha_emision || '').slice(0, 10);
        if (fFechaIni && fecha < fFechaIni) return false;
        if (fFechaFin && fecha > fFechaFin) return false;
      }
      return true;
    });
  }, [ocsLista, fSucursal, fSerie, fNumero, fRuc, fEstado, fFechaOn, fFechaIni, fFechaFin]);

  const sucursalesOcs  = useMemo(() => [...new Set(ocsLista.map(o => o.sucursal_nombre).filter(Boolean))].sort(), [ocsLista]);
  const seriesOcs      = useMemo(() => [...new Set(ocsLista.map(o => o.serie_codigo).filter(Boolean))].sort(), [ocsLista]);
  const proveedoresOcs = useMemo(() => {
    const map = new Map();
    ocsLista.forEach(o => {
      if (o.proveedor_ruc && !map.has(o.proveedor_ruc))
        map.set(o.proveedor_ruc, { value: o.proveedor_ruc, label: o.proveedor_ruc, sublabel: o.proveedor_nombre || '' });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [ocsLista]);

  const limpiarFiltros = () => { setFSucursal(''); setFSerie(''); setFNumero(''); setFRuc(''); setFEstado(''); setFFechaOn(true); setFFechaIni(weekAgo()); setFFechaFin(today()); };
  const hayFiltros = !!(fSucursal || fSerie || fNumero || fRuc || fEstado);

  /* ── Ítems visibles ──────────────────────────────────────────────── */
  const itemsVisibles = useMemo(() => {
    if (soloPendientes)
      return ocItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
    return ocItems;
  }, [ocItems, soloPendientes]);

  /* ── Selección ───────────────────────────────────────────────────── */
  const toggleItem = (it) => {
    const key = it.id_detalle;
    setSeleccionados(prev => {
      if (key in prev) { const n = { ...prev }; delete n[key]; return n; }
      const pend = parseFloat(it.cantidad_pendiente) || 0;
      return { ...prev, [key]: pend > 0 ? pend : parseFloat(it.cantidad) || 1 };
    });
  };

  const setCantidadItem = (id_detalle, val) =>
    setSeleccionados(prev => ({ ...prev, [id_detalle]: val }));

  const toggleAll = () => {
    const aptos = itemsVisibles.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
    const todos = aptos.length > 0 && aptos.every(it => it.id_detalle in seleccionados);
    if (todos) {
      setSeleccionados(prev => { const n = { ...prev }; aptos.forEach(it => delete n[it.id_detalle]); return n; });
    } else {
      const add = {};
      aptos.forEach(it => { if (!(it.id_detalle in seleccionados)) add[it.id_detalle] = parseFloat(it.cantidad_pendiente) || 1; });
      setSeleccionados(prev => ({ ...prev, ...add }));
    }
  };

  /* ── Totales ─────────────────────────────────────────────────────── */
  const totales = useMemo(() => {
    let gravado = 0, exonerado = 0, igv = 0;
    ocItems.forEach(it => {
      if (!(it.id_detalle in seleccionados)) return;
      const qty  = parseFloat(seleccionados[it.id_detalle]) || 0;
      const cu   = parseFloat(it.precio_soles) || 0;
      const pct  = parseFloat(it.porcentaje_igv) || 0;
      const igvU = cu * pct / 100;
      if (pct > 0) gravado   += cu * qty;
      else          exonerado += cu * qty;
      igv += igvU * qty;
    });
    return { gravado, exonerado, igv, total: gravado + exonerado + igv };
  }, [ocItems, seleccionados]);

  const setF    = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const req     = (flag, label) => flag ? `${label} *` : label;
  const tipoMov = tiposMovimiento.find(t => String(t.id_tipo_movimiento) === String(form.id_tipo_movimiento)) || null;

  /* ── Guardar ─────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!form.id_almacen)         { setAlert({ type: 'warning', message: 'Seleccione un almacén.' }); return; }
    if (!form.id_tipo_movimiento) { setAlert({ type: 'warning', message: 'Seleccione un tipo de movimiento.' }); return; }
    if (!form.fecha_operacion)    { setAlert({ type: 'warning', message: 'Ingrese la fecha de operación.' }); return; }

    const itemsSel = ocItems.filter(it => it.id_detalle in seleccionados);
    if (itemsSel.length === 0) { setAlert({ type: 'warning', message: 'Seleccione al menos un artículo.' }); return; }

    for (const it of itemsSel) {
      const qty      = parseFloat(seleccionados[it.id_detalle]) || 0;
      const pendiente = parseFloat(it.cantidad_pendiente) || 0;
      if (qty <= 0)        { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" debe ser mayor a 0.` }); return; }
      if (qty > pendiente) { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" excede la pendiente (${fmtN(pendiente, 4)}).` }); return; }
    }

    const n = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;
    const payload = {
      id_almacen:         n(form.id_almacen),
      id_sucursal:        n(form.id_sucursal),
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      id_proveedor:       ocSeleccionada.id_proveedor || null,
      ruc_proveedor:      ocSeleccionada.proveedor_ruc || null,
      fecha_operacion:    form.fecha_operacion,
      id_tipo_doc_ref1:   n(form.id_tipo_doc_ref1),
      num_doc_ref1:       form.num_doc_ref1 || null,
      id_tipo_doc_ref2:   n(form.id_tipo_doc_ref2),
      num_doc_ref2:       form.num_doc_ref2 || null,
      observacion:        form.observacion || null,
      id_centro_costo:    n(form.id_centro_costo),
      id_solicitante:     n(form.id_solicitante),
      id_orden_compra:    ocSeleccionada.id_orden,
      detalle: itemsSel.map((it, i) => ({
        orden:          i + 1,
        id_articulo:    it.id_articulo,
        id_unidad:      it.id_unidad || null,
        cantidad:       parseFloat(seleccionados[it.id_detalle]) || 1,
        costo_unitario: parseFloat(it.precio_soles) || 0,
        igv_pct:        parseFloat(it.porcentaje_igv) || 0,
        id_detalle_oc:  it.id_detalle,
      })),
    };

    setSaving(true);
    try {
      const res = await parteEntradaService.create(payload);
      setCreado(res.data);
      fetchOcsLista();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar la parte de entrada.') });
    } finally {
      setSaving(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     COLUMNAS LISTA DE OCs
  ════════════════════════════════════════════════════════════════ */
  const cellSm = { fontSize: 11 };
  const cellXs = { fontSize: 10 };

  const tableColumns = [
    { key: 'sucursal_nombre', label: 'Suc.', width: 72, style: { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 72 } },
    { key: 'serie_codigo',    label: 'Serie',  width: 68, style: cellSm },
    { key: 'numero', label: 'Número', width: 80,
      render: r => <span style={{ fontWeight: 700, color: '#60a5fa', fontSize: 12 }}>{r.numero}</span> },
    { key: 'proveedor_nombre', label: 'Proveedor',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
          {r.proveedor_ruc && <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>{r.proveedor_ruc}</span>}
          {r.proveedor_ruc && r.proveedor_nombre && <span style={{ color: '#374151', flexShrink: 0 }}>—</span>}
          <span style={{ fontSize: 11, color: '#e2e8f0', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.proveedor_nombre || '—'}</span>
        </div>
      )},
    { key: 'fecha_emision', label: 'F. Emisión', width: 90, style: cellSm,
      render: r => r.fecha_emision?.slice(0, 10) || '—' },
    { key: 'estado', label: 'Estado OC', width: 68,
      render: r => <span style={{ background: ESTADO_OC[r.estado]?.bg, color: ESTADO_OC[r.estado]?.color, border: `1px solid ${ESTADO_OC[r.estado]?.border}`, borderRadius: 8, padding: '2px 6px', fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block' }}>{r.estado}</span> },
    { key: 'atencion_pe', label: 'Atención PE', width: 90,
      render: r => <span style={{ background: ATENCION_PE[r.atencion_pe]?.bg, color: ATENCION_PE[r.atencion_pe]?.color, border: `1px solid ${ATENCION_PE[r.atencion_pe]?.border}`, borderRadius: 8, padding: '2px 6px', fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', display: 'inline-block' }}>{r.atencion_pe}</span> },
    { key: '_accion', label: 'Acción', width: 84,
      render: r => {
        const atendidoTotal = r.atencion_pe === 'ATENDIDO TOTAL';
        return (
          <button
            onClick={() => !atendidoTotal && seleccionarOc(r)}
            disabled={atendidoTotal}
            style={{
              fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
              border: '1px solid', cursor: atendidoTotal ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
              background: atendidoTotal ? 'rgba(255,255,255,0.03)' : 'rgba(96,165,250,0.12)',
              color: atendidoTotal ? '#374151' : '#60a5fa',
              borderColor: atendidoTotal ? 'rgba(255,255,255,0.06)' : 'rgba(96,165,250,0.35)',
            }}
            onMouseEnter={e => { if (!atendidoTotal) { e.currentTarget.style.background = 'rgba(96,165,250,0.22)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.6)'; }}}
            onMouseLeave={e => { if (!atendidoTotal) { e.currentTarget.style.background = 'rgba(96,165,250,0.12)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.35)'; }}}
          >
            {atendidoTotal ? '✓ Listo' : 'Generar PE'}
          </button>
        );
      }
    },
  ];

  /* ════════════════════════════════════════════════════════════════
     VISTA LISTA
  ════════════════════════════════════════════════════════════════ */
  if (view === 'list') {
    const inputSm = {
      height: 30, padding: '0 9px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
      color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%',
    };

    return (
      <Layout title="PE desde OC" subtitle="Almacén">
        {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
        <div style={{ padding: '0 0 16px' }}>
          <div className="oc-page-header">
            <h2>PE desde Orden de Compra</h2>
            <div className="oc-header-actions">
              <button className="btn btn-secondary btn-sm" onClick={fetchOcsLista}>↺ Actualizar</button>
            </div>
          </div>

          {/* ── Barra de filtros ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '120px 0.7fr 0.6fr 1fr 1.1fr auto auto',
            gap: 10, marginBottom: 10, alignItems: 'end',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 10, padding: '12px 16px',
          }}>
            {/* Sucursal */}
            <FilterSelect label="Sucursal" value={fSucursal} onChange={setFSucursal} options={sucursalesOcs} />

            {/* Serie */}
            <FilterAC label="Serie" value={fSerie} onChange={setFSerie} options={seriesOcs} placeholder="Buscar serie..." />

            {/* Número */}
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Número</label>
              <input value={fNumero} onChange={e => setFNumero(e.target.value)} placeholder="Buscar..." style={{
                ...inputSm,
                background: fNumero ? 'rgba(96,165,250,0.08)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${fNumero ? 'rgba(96,165,250,0.35)' : 'rgba(255,255,255,0.09)'}`,
              }}
                onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.12)'; }}
                onBlur={e  => { e.target.style.borderColor = fNumero ? 'rgba(96,165,250,0.35)' : 'rgba(255,255,255,0.09)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* RUC + Razón Social */}
            <FilterAC label="Proveedor / RUC" value={fRuc} onChange={setFRuc} optionItems={proveedoresOcs} placeholder="Buscar por RUC o razón social..." />

            {/* Estado Atención PE */}
            <FilterEstado value={fEstado} onChange={setFEstado} />

            {/* Fecha — rango con checkbox */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Label + checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 10, color: fFechaOn ? '#64748b' : '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                  Fecha Emisión
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 'auto' }}>
                  <input
                    type="checkbox" checked={fFechaOn} onChange={e => setFFechaOn(e.target.checked)}
                    style={{ accentColor: '#3b82f6', width: 12, height: 12, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 9.5, color: fFechaOn ? '#60a5fa' : '#374151', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {fFechaOn ? 'activo' : 'inactivo'}
                  </span>
                </label>
              </div>
              {/* Inputs de rango */}
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', opacity: fFechaOn ? 1 : 0.3, pointerEvents: fFechaOn ? 'auto' : 'none' }}>
                <input type="date" value={fFechaIni} onChange={e => setFFechaIni(e.target.value)} style={{
                  ...inputSm, width: '100%',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
                }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.09)'}
                />
                <span style={{ color: '#374151', fontSize: 11, flexShrink: 0 }}>—</span>
                <input type="date" value={fFechaFin} onChange={e => setFFechaFin(e.target.value)} style={{
                  ...inputSm, width: '100%',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
                }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.09)'}
                />
              </div>
            </div>

            {/* Limpiar */}
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={limpiarFiltros}
                disabled={!hayFiltros}
                title="Limpiar filtros"
                style={{
                  height: 30, padding: '0 12px', fontSize: 11, fontWeight: 600, borderRadius: 7,
                  border: '1px solid', cursor: hayFiltros ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
                  background: hayFiltros ? 'rgba(251,146,60,0.1)' : 'rgba(255,255,255,0.03)',
                  color:       hayFiltros ? '#fb923c' : '#374151',
                  borderColor: hayFiltros ? 'rgba(251,146,60,0.3)' : 'rgba(255,255,255,0.07)',
                  transition: 'all 0.15s',
                }}
              >✕ Limpiar</button>
            </div>
          </div>

          {/* Conteo de resultados */}
          {(hayFiltros || fFechaOn) && (
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, paddingLeft: 2 }}>
              {ocsListaFiltrada.length} resultado{ocsListaFiltrada.length !== 1 ? 's' : ''} de {ocsLista.length} órdenes
            </div>
          )}

          <DataTable
            columns={tableColumns}
            data={ocsListaFiltrada}
            loading={loadingList}
            hideSearch
            hideToolbarRight
          />
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     PANTALLA DE ÉXITO
  ════════════════════════════════════════════════════════════════ */
  if (creado) {
    const ec = ESTADO_PE[creado.estado] || {};
    return (
      <>
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>Parte de Entrada generado</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            PE-{String(creado.numero).padStart(6, '0')}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20 }}>
            {creado.almacen_nombre} · {creado.fecha_operacion?.split('T')[0] || creado.fecha_operacion}
          </div>
          <Badge label={creado.estado} cfg={ec} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={volverALista}>Ver lista de OCs</button>
            <button onClick={() => setImprimiendo(true)} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '8px 18px', fontSize: 13, fontWeight: 600,
              borderRadius: 8, border: '1px solid rgba(96,165,250,0.4)', cursor: 'pointer',
              background: 'rgba(96,165,250,0.12)', color: '#60a5fa', fontFamily: 'Inter, sans-serif',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
              </svg>
              Imprimir
            </button>
            <button className="btn btn-primary" onClick={() => { setCreado(null); seleccionarOc(ocSeleccionada); }}>+ Nuevo PE desde misma OC</button>
          </div>
        </div>
      </Layout>
      {imprimiendo && (
        <ImprimirParteModal
          tipo="entrada"
          parte={creado}
          trabajadores={trabajadores}
          onClose={() => setImprimiendo(false)}
        />
      )}
      </>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA FORMULARIO
  ════════════════════════════════════════════════════════════════ */
  const aptosPendientes    = itemsVisibles.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
  const todosSeleccionados = aptosPendientes.length > 0 && aptosPendientes.every(it => it.id_detalle in seleccionados);
  const nSel = Object.keys(seleccionados).length;

  return (
    <Layout>
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="rq-page">

        {/* ── Header ── */}
        <div className="rq-header">
          <div className="rq-header-left">
            <button className="oc-back-btn" onClick={volverALista}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <div className="rq-header-title">
              <div className="rq-header-meta">Almacén — PE desde OC</div>
              <div className="rq-header-name">
                {ocSeleccionada ? ocRef(ocSeleccionada.serie_codigo, ocSeleccionada.numero) : 'Generar Parte de Entrada'}
              </div>
            </div>
          </div>
          <div className="rq-header-actions">
            {nextNumero && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>Correlativo</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#60a5fa', letterSpacing: 1.5 }}>
                  PE-{String(nextNumero).padStart(6, '0')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="rq-body" style={{ overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* ── SECCIÓN 1: Info de la OC (solo lectura) ── */}
            {ocSeleccionada && (
              <div className="rq-card">
                <div className="rq-card-title">Orden de Compra</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '0 14px' }}>
                  <div className="form-row">
                    <label>Número OC</label>
                    <input value={ocRef(ocSeleccionada.serie_codigo, ocSeleccionada.numero)} readOnly style={{ opacity: 0.75, cursor: 'default', fontWeight: 700, color: '#60a5fa' }} />
                  </div>
                  <div className="form-row">
                    <label>Proveedor</label>
                    <input value={`${ocSeleccionada.proveedor_ruc || ''}${ocSeleccionada.proveedor_ruc && ocSeleccionada.proveedor_nombre ? ' — ' : ''}${ocSeleccionada.proveedor_nombre || ''}`} readOnly style={{ opacity: 0.65, cursor: 'default' }} />
                  </div>
                  <div className="form-row">
                    <label>Fecha Emisión</label>
                    <input value={ocSeleccionada.fecha_emision?.slice(0, 10) || ''} readOnly style={{ opacity: 0.65, cursor: 'default' }} />
                  </div>
                </div>
              </div>
            )}

            {/* ── SECCIÓN 2: Identificación ── */}
            <div className="rq-card">
              <div className="rq-card-title">Identificación del Parte de Entrada</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.6fr 0.85fr', gap: '0 14px', alignItems: 'start' }}>
                <AutoComplete
                  label="Sucursal"
                  value={String(form.id_sucursal || '')}
                  options={sucursales} idField="id_sucursal" displayField="nombre"
                  onChange={v => setF('id_sucursal', v)} placeholder="Seleccionar sucursal..."
                />
                <AutoComplete
                  label="Almacén *"
                  value={String(form.id_almacen || '')}
                  options={almacenesSucursal} idField="id_almacen" displayField="nombre"
                  onChange={v => setF('id_almacen', v)} placeholder="Seleccionar almacén..."
                />
                <AutoComplete
                  label="Tipo de Movimiento *"
                  value={String(form.id_tipo_movimiento || '')}
                  options={tiposMovimiento} idField="id_tipo_movimiento"
                  displayField={t => `[${t.codigo}] ${t.descripcion}`}
                  onChange={v => setF('id_tipo_movimiento', v)} placeholder="Seleccionar tipo..."
                />
                <div className="form-row">
                  <label>Fecha Operación *</label>
                  <input type="date" value={form.fecha_operacion}
                    onChange={e => setF('fecha_operacion', e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 3: Referencias ── */}
            <div className="rq-card">
              <div className="rq-card-title">Referencias Documentales</div>
              <div className="rq-prov-obs-grid">
                <AutoComplete
                  label={req(tipoMov?.obliga_doc_ref1, 'Doc. Ref. 1')}
                  value={String(form.id_tipo_doc_ref1 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref1', v)} placeholder="Tipo documento..."
                />
                <div className="form-row">
                  <label>{req(tipoMov?.obliga_num_ref1, 'Nº Ref. 1')}</label>
                  <input value={form.num_doc_ref1} onChange={e => setF('num_doc_ref1', e.target.value)} placeholder="Número referencia..." />
                </div>
                <AutoComplete
                  label={req(tipoMov?.obliga_doc_ref2, 'Doc. Ref. 2')}
                  value={String(form.id_tipo_doc_ref2 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref2', v)} placeholder="Tipo documento..."
                />
                <div className="form-row">
                  <label>{req(tipoMov?.obliga_num_ref2, 'Nº Ref. 2')}</label>
                  <input value={form.num_doc_ref2} onChange={e => setF('num_doc_ref2', e.target.value)} placeholder="Número referencia..." />
                </div>
                <AutoComplete
                  label={req(tipoMov?.obliga_centro_costo, 'Centro de Costo')}
                  value={String(form.id_centro_costo || '')}
                  options={centrosCosto} idField="id_centro_costo" displayField="descripcion"
                  onChange={v => setF('id_centro_costo', v)} placeholder="Centro de costo..."
                  twoLine
                />
                <AutoComplete
                  label={req(tipoMov?.obliga_solicitante, 'Solicitante')}
                  value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setF('id_solicitante', v)} placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                />
              </div>
              <div className="form-row" style={{ marginTop: 6 }}>
                <label>{req(tipoMov?.obliga_glosa, 'Observaciones / Glosa')}</label>
                <textarea value={form.observacion} onChange={e => setF('observacion', e.target.value)}
                  rows={2} placeholder="Observaciones del parte de entrada..."
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                    fontSize: 12.5, borderRadius: 8, resize: 'vertical',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)',
                    color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                  onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* ── SECCIÓN 4: Ítems de la OC ── */}
            <div className="rq-card" style={{ flex: 'none' }}>
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos de la OC</span>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                    <button onClick={() => setSoloPendientes(false)} style={{
                      fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s',
                      background: !soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
                      color:      !soloPendientes ? '#60a5fa' : '#64748b',
                      borderColor:!soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)',
                    }}>Todos</button>
                    <button onClick={() => setSoloPendientes(true)} style={{
                      fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s',
                      background: soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
                      color:      soloPendientes ? '#60a5fa' : '#64748b',
                      borderColor:soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)',
                    }}>Solo pendientes</button>
                  </div>
                </div>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {nSel > 0 ? `${nSel} ítem${nSel !== 1 ? 's' : ''} marcado${nSel !== 1 ? 's' : ''}` : 'Sin selección'}
                </span>
              </div>

              {loadingItems && <div style={{ textAlign: 'center', padding: 28, color: '#64748b', fontSize: 13 }}>Cargando artículos...</div>}

              {!loadingItems && (
                <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, overflowX: 'auto', background: 'rgba(0,0,0,0.12)', minHeight: 80 }}>
                  <table className="rq-articles" style={{ minWidth: 960 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36, textAlign: 'center' }}>
                          <input type="checkbox" checked={todosSeleccionados} onChange={toggleAll}
                            title="Seleccionar todos los pendientes"
                            style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 13, height: 13 }} />
                        </th>
                        <th style={{ width: 32 }}>#</th>
                        <th style={{ width: 108 }}>Código</th>
                        <th>Artículo / UM</th>
                        <th style={{ width: 100, textAlign: 'center' }}>RQ Origen</th>
                        <th style={{ width: 76, textAlign: 'right' }}>Cant. OC</th>
                        <th style={{ width: 76, textAlign: 'right' }}>Recibido</th>
                        <th style={{ width: 80, textAlign: 'right' }}>Pendiente</th>
                        <th style={{ width: 105, textAlign: 'right' }}>A recibir ✎</th>
                        <th style={{ width: 96, textAlign: 'right' }}>Costo Unit.</th>
                        <th style={{ width: 50, textAlign: 'center' }}>%IGV</th>
                        <th style={{ width: 80, textAlign: 'right' }}>IGV Unit.</th>
                        <th style={{ width: 90, textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsVisibles.length === 0 && (
                        <tr><td colSpan={13} className="rq-empty">
                          {soloPendientes ? 'No hay artículos pendientes.' : 'Esta OC no tiene artículos.'}
                        </td></tr>
                      )}
                      {itemsVisibles.map((it, i) => {
                        const pendiente  = parseFloat(it.cantidad_pendiente) || 0;
                        const recibido   = parseFloat(it.cantidad_recibida)  || 0;
                        const totalOc    = parseFloat(it.cantidad)           || 0;
                        const agotado    = pendiente <= 0;
                        const checked    = it.id_detalle in seleccionados;
                        const cantRecibir = checked ? (parseFloat(seleccionados[it.id_detalle]) || 0) : 0;
                        const cu         = parseFloat(it.precio_soles)   || 0;
                        const pct        = parseFloat(it.porcentaje_igv) || 0;
                        const igvU       = cu * pct / 100;
                        const totalLinea = (cu + igvU) * cantRecibir;
                        const rqLabel    = it.rq_numero
                          ? `${it.rq_serie ? it.rq_serie.trim() + '-' : ''}${it.rq_numero}`
                          : null;

                        return (
                          <tr key={it.id_detalle}
                            style={{ opacity: agotado ? 0.4 : 1, background: checked ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
                            {/* Checkbox */}
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {agotado
                                ? <span title="Completamente recibido" style={{ fontSize: 14, color: '#22c55e' }}>✓</span>
                                : (
                                  <input type="checkbox" checked={checked} onChange={() => toggleItem(it)}
                                    style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 14, height: 14 }} />
                                )
                              }
                            </td>
                            <td className="rq-td-num">{i + 1}</td>
                            {/* Código */}
                            <td style={{ padding: '4px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10.5, whiteSpace: 'nowrap' }}>
                              {it.articulo_codigo || '—'}
                            </td>
                            {/* Artículo + UM */}
                            <td style={{ padding: '4px 8px', minWidth: 160 }}>
                              <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{it.articulo_descripcion || '—'}</div>
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{it.unidad_codigo || ''}</div>
                            </td>
                            {/* RQ Origen */}
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {rqLabel
                                ? <span style={{ fontSize: 10.5, fontWeight: 700, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{rqLabel}</span>
                                : <span style={{ color: '#374151', fontSize: 10 }}>—</span>}
                            </td>
                            {/* Cant. OC */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(totalOc, 4)}</td>
                            {/* Recibido */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: recibido > 0 ? '#fb923c' : '#475569', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(recibido, 4)}</td>
                            {/* Pendiente */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: agotado ? '#22c55e' : '#e2e8f0', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(pendiente, 4)}</td>
                            {/* A recibir (editable) */}
                            <td style={{ padding: '3px 6px' }}>
                              {checked
                                ? <NumInput value={seleccionados[it.id_detalle]} max={pendiente}
                                    onChange={e => setCantidadItem(it.id_detalle, e.target.value)} />
                                : <span style={{ color: '#374151', fontSize: 11, display: 'block', textAlign: 'right' }}>—</span>
                              }
                            </td>
                            {/* Costo Unit. (bloqueado) */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', fontSize: 11.5, whiteSpace: 'nowrap' }}>S/ {fmtN(cu, 4)}</td>
                            {/* %IGV */}
                            <td style={{ textAlign: 'center', padding: '4px 6px', color: '#94a3b8', fontSize: 11.5 }}>{pct}%</td>
                            {/* IGV Unit. */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', fontSize: 11.5, whiteSpace: 'nowrap' }}>S/ {fmtN(igvU, 4)}</td>
                            {/* Total */}
                            <td style={{ textAlign: 'right', padding: '4px 8px', fontWeight: checked ? 700 : 400, color: checked ? '#e2e8f0' : '#374151', fontVariantNumeric: 'tabular-nums', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {checked ? `S/ ${fmt(totalLinea)}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Resumen de Totales ── */}
            {nSel > 0 && (
              <div className="oc-totales" style={{ marginBottom: 4 }}>
                <div className="oc-totales-card">
                  {totales.gravado > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label">Base Imponible</span>
                      <span className="oc-total-value">{fmt(totales.gravado)}</span>
                    </div>
                  )}
                  {totales.exonerado > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label">Exonerado</span>
                      <span className="oc-total-value">{fmt(totales.exonerado)}</span>
                    </div>
                  )}
                  {totales.igv > 0 && (
                    <div className="oc-total-row">
                      <span className="oc-total-label" style={{ color: '#60a5fa' }}>IGV</span>
                      <span className="oc-total-value" style={{ color: '#60a5fa' }}>{fmt(totales.igv)}</span>
                    </div>
                  )}
                  <div className="oc-total-row main">
                    <span className="oc-total-label">Total a Ingresar</span>
                    <span className="oc-total-value">S/ {fmt(totales.total)}</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>{/* fin rq-body */}

        {/* ── Footer ── */}
        <div style={{
          flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.07)',
          padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 10, background: 'rgba(0,0,0,0.18)',
        }}>
          <span style={{ fontSize: 11, color: '#475569' }}>
            {nSel > 0
              ? `${nSel} ítem${nSel !== 1 ? 's' : ''} seleccionado${nSel !== 1 ? 's' : ''} — Solo los marcados generarán ingreso`
              : 'Marque los artículos a ingresar con el ✓ de la tabla'}
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={volverALista}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSubmit}
              disabled={saving || loadingCat || nSel === 0} style={{ minWidth: 200 }}>
              {saving ? 'Guardando...' : `Generar Parte de Entrada (${nSel} ítem${nSel !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}
